import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RiskBadge from "@/components/RiskBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search as SearchIcon, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const LOCALITIES = [
  "All",
  "MG Road",
  "Indiranagar",
  "Koramangala",
  "Whitefield",
  "HSR Layout",
  "Electronic City",
  "JP Nagar",
  "BTM Layout",
  "Brigade Road",
  "Marathahalli",
  "Jayanagar",
  "UB City",
  "Sadashivanagar",
];

export default function Advertisements() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [level, setLevel] = useState(params.get("level") || "all");
  const [locality, setLocality] = useState(params.get("locality") || "All");
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const query = {};
      if (q) query.q = q;
      if (level && level !== "all") query.level = level;
      if (locality && locality !== "All") query.locality = locality;
      const { data } = await api.get("/ads", { params: query });
      setAds(data);
    } finally {
      setLoading(false);
    }
  }, [q, level, locality]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  const onSearch = (e) => {
    e?.preventDefault();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (level !== "all") next.set("level", level);
    if (locality !== "All") next.set("locality", locality);
    setParams(next);
    fetchAds();
  };

  const reseed = async () => {
    try {
      const { data } = await api.post("/ads/seed");
      toast.success(`Reseeded ${data.seeded} advertisements.`);
      fetchAds();
    } catch (err) {
      toast.error("Reseed failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="ads-page">
      <header className="flex flex-col gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
            Advertisements
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl">
            Search collected advertisements
          </h1>
          <p className="text-slate-500 text-sm mt-2">
            Filter by keyword, locality and assessed risk level.
          </p>
        </div>
        <Button
          data-testid="reseed-btn"
          variant="outline"
          size="sm"
          onClick={reseed}
          className="gap-2 self-start sm:self-end"
        >
          <RefreshCcw className="h-4 w-4" /> Re-seed sample data
        </Button>
      </header>

      <form
        onSubmit={onSearch}
        className="grid grid-cols-1 sm:grid-cols-[1fr_180px_160px_auto] gap-3 sm:items-end border border-slate-200 bg-white rounded-md p-4"
      >
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
            Keyword
          </label>
          <div className="relative mt-1">
            <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="ad-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. hotel, massage, young"
              className="pl-9"
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
            Locality
          </label>
          <Select value={locality} onValueChange={setLocality}>
            <SelectTrigger data-testid="locality-select" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCALITIES.map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
            Risk level
          </label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="risk-select" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="ad-search-submit"
          type="submit"
          className="bg-slate-900 hover:bg-slate-800 h-10 px-5"
        >
          Apply
        </Button>
      </form>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_120px_90px_110px] px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-mono bg-slate-50 border-b border-slate-200">
          <span>Title & Locality</span>
          <span>Posted</span>
          <span>Phones</span>
          <span className="text-right">Risk</span>
        </div>
        <div>
          {loading && (
            <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>
          )}
          {!loading && ads.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500">
              No advertisements match the current filters.
            </div>
          )}
          {ads.map((a) => (
            <button
              key={a.id}
              data-testid={`ad-row-${a.id}`}
              onClick={() => navigate(`/ads/${a.id}`)}
              className="w-full text-left flex flex-col gap-2 sm:grid sm:grid-cols-[1fr_120px_90px_110px] sm:items-center px-4 py-3 border-b border-slate-100 hover:bg-slate-50"
            >
              <div className="min-w-0">
                <div className="flex items-start justify-between gap-2 sm:block">
                  <div className="text-sm font-medium truncate flex-1">{a.title}</div>
                  <span className="sm:hidden shrink-0">
                    <RiskBadge level={a.risk.level} score={a.risk.score} />
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  {a.locality || "Unknown"} · {(a.text || "").slice(0, 90)}…
                </div>
                <div className="sm:hidden mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 font-mono">
                  <span>{(a.posted_at || "").slice(0, 10)}</span>
                  <span>·</span>
                  <span>{(a.phones || []).length} phone{(a.phones || []).length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <div className="hidden sm:block text-xs font-mono text-slate-500">
                {(a.posted_at || "").slice(0, 10)}
              </div>
              <div className="hidden sm:block text-xs font-mono text-slate-700">
                {(a.phones || []).length}
              </div>
              <div className="hidden sm:flex justify-end">
                <RiskBadge level={a.risk.level} score={a.risk.score} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
